import React, { createContext, useContext, useEffect, useState } from "react";

// UI-string dictionary
const STRINGS = {
  en: {
    // Navbar
    cart: "Cart",
    farmers_market: "Farmer's Market",
    // Filters
    search_placeholder: "Search apples, spinach, mango…",
    all: "All",
    only_offers: "Only offers",
    clear: "Clear",
    // Category headers
    items: "items",
    // Loading / empty
    loading: "Loading fresh picks…",
    empty_filtered: "No matches for your filters.",
    empty_none: "No products yet — the admin is stocking the shelves.",
    // Product card
    add: "Add",
    off: "OFF",
    qty_placeholder: "e.g. 3.2",
    qty_hint: "Input any quantity you want. 0.5Kg, 200gm, 1.2 dozen, 3 pcs etc.",
    add_success: "Added",
    invalid_qty: "Enter a valid quantity",
    // Footer
    footer_admin: "Admin →",
    footer_tagline: "Handpicked with care.",
    // Checkout
    checkout: "Checkout",
    delivery_details: "Delivery details",
    full_name: "Full name",
    mobile_number: "Mobile number *",
    email: "Email *",
    delivery_address: "Delivery address *",
    address_placeholder: "Flat, street, city, pincode",
    your_basket: "Your basket",
    total: "Total",
    pay: "Pay",
    place_order: "Place Order",
    cod_note: "Cash on Delivery — pay in cash when your order arrives.",
    remove: "Remove",
    empty_cart: "Your cart is empty.",
    continue_shopping: "Continue shopping",
    stripe_note: "You'll be taken to Stripe to complete payment securely.",
    // Payment status
    confirming: "Placing your order…",
    payment_takes: "This takes a moment.",
    order_placed: "Order Placed",
    thank_you: "Thank you. We'll deliver ₹{amount} of fresh goodness to your door. Please keep the cash ready.",
    order_number: "Order",
    back_to_shop: "Back to shop",
    payment_unclear: "Payment status unclear",
    payment_unclear_desc: "We couldn't confirm the payment. If money was debited, it will reflect shortly.",
    payment_cancelled: "Payment cancelled",
    basket_safe: "Your basket is safe — pick up where you left off.",
    retry_checkout: "Retry checkout",
  },
  ta: {
    cart: "கார்ட்",
    farmers_market: "விவசாய சந்தை",
    search_placeholder: "ஆப்பிள், கீரை, மாம்பழம் தேடுங்கள்…",
    all: "அனைத்தும்",
    only_offers: "சலுகைகள் மட்டும்",
    clear: "அழிக்கவும்",
    items: "பொருட்கள்",
    loading: "புதிய பொருட்களை ஏற்றுகிறது…",
    empty_filtered: "உங்கள் தேர்வுக்கு எதுவும் இல்லை.",
    empty_none: "இன்னும் தயாரிப்புகள் இல்லை — நிர்வாகி பொருட்களைச் சேர்க்கிறார்.",
    add: "சேர்",
    off: "தள்ளுபடி",
    qty_placeholder: "எ.கா. 3.2",
    qty_hint: "நீங்கள் விரும்பும் அளவை உள்ளிடவும். 0.5கிலோ, 200கிராம், 1.2 டஜன், 3 துண்டுகள் போன்றவை.",
    add_success: "சேர்க்கப்பட்டது",
    invalid_qty: "சரியான அளவை உள்ளிடவும்",
    footer_admin: "நிர்வாகி →",
    footer_tagline: "அன்புடன் கையால் தேர்வு செய்யப்பட்டது.",
    checkout: "செலுத்துதல்",
    delivery_details: "விநியோக விவரங்கள்",
    full_name: "முழு பெயர்",
    mobile_number: "மொபைல் எண் *",
    email: "மின்னஞ்சல் *",
    delivery_address: "விநியோக முகவரி *",
    address_placeholder: "வீடு, தெரு, நகரம், அஞ்சல் குறியீடு",
    your_basket: "உங்கள் கூடை",
    total: "மொத்தம்",
    pay: "செலுத்து",
    place_order: "ஆர்டர் செய்",
    cod_note: "பணம் கைமாற்று — உங்கள் ஆர்டர் வந்தவுடன் பணம் செலுத்தவும்.",
    remove: "நீக்கு",
    empty_cart: "உங்கள் கூடை காலியாக உள்ளது.",
    continue_shopping: "மேலும் வாங்கவும்",
    stripe_note: "பாதுகாப்பாக செலுத்த Stripe பக்கத்திற்குச் செல்வீர்கள்.",
    confirming: "உங்கள் ஆர்டரை பதிவு செய்கிறது…",
    payment_takes: "ஒரு நொடி…",
    order_placed: "ஆர்டர் பதிவானது",
    thank_you: "நன்றி. ₹{amount} மதிப்பிலான புதிய பொருட்களை உங்கள் வீட்டிற்கு விநியோகிப்போம். பணத்தை தயாராக வைக்கவும்.",
    order_number: "ஆர்டர்",
    back_to_shop: "கடைக்கு திரும்பு",
    payment_unclear: "பணம் செலுத்துதல் நிலை தெளிவாக இல்லை",
    payment_unclear_desc: "பணம் செலுத்தலை உறுதிப்படுத்த முடியவில்லை. கட்டணம் விலக்கப்பட்டிருந்தால், விரைவில் பிரதிபலிக்கும்.",
    payment_cancelled: "பணம் செலுத்துதல் ரத்து செய்யப்பட்டது",
    basket_safe: "உங்கள் கூடை பாதுகாப்பாக உள்ளது — நீங்கள் விட்ட இடத்திலிருந்து தொடரவும்.",
    retry_checkout: "மீண்டும் முயற்சி",
  },
};

// UoM (Unit of Measure) Tamil translations
export const UOM_TA = {
  kg: "கிலோ",
  gm: "கிராம்",
  piece: "துண்டு",
  pieces: "துண்டுகள்",
  packet: "பாக்கெட்",
  packets: "பாக்கெட்டுகள்",
  ltr: "லிட்டர்",
  ml: "மில்லி",
  dozen: "டஜன்",
  bunch: "கட்டு",
};

// Fallback product name translations (for products the admin has not translated yet)
const PRODUCT_TA = {
  Apple: "ஆப்பிள்",
  Banana: "வாழைப்பழம்",
  "Alphonso Mango": "மாம்பழம்",
  Carrot: "காரட்",
  Tomato: "தக்காளி",
  Potato: "உருளைக்கிழங்கு",
  Spinach: "பசலைக்கீரை",
  Coriander: "கொத்தமல்லி",
};

const LangCtx = createContext({ lang: "en", setLang: () => {} });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      return localStorage.getItem("ng_lang") || "en";
    } catch {
      return "en";
    }
  });
  const setLang = (l) => {
    setLangState(l);
    try { localStorage.setItem("ng_lang", l); } catch {}
    document.documentElement.setAttribute("lang", l);
  };
  useEffect(() => {
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);
  return <LangCtx.Provider value={{ lang, setLang }}>{children}</LangCtx.Provider>;
}

export function useLang() {
  return useContext(LangCtx);
}

// String translator with {vars} interpolation
export function useT() {
  const { lang } = useLang();
  return (key, vars) => {
    const table = STRINGS[lang] || STRINGS.en;
    let s = table[key] ?? STRINGS.en[key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) s = s.replace(`{${k}}`, v);
    }
    return s;
  };
}

// Data-object helpers
export const productName = (product, lang) => {
  if (lang !== "ta") return product.name;
  return product.name_ta || PRODUCT_TA[product.name] || product.name;
};

export const productDescription = (product, lang) => {
  if (lang !== "ta") return product.description || "";
  return product.description_ta || product.description || "";
};

export const categoryName = (cat, lang) => {
  if (lang !== "ta") return cat.name;
  return cat.name_ta || cat.name;
};

export const offerLabel = (offer, lang) => {
  if (!offer) return "";
  if (lang !== "ta") return offer.label || "";
  return offer.label_ta || offer.label || "";
};

export const uomLabel = (uom, lang) => {
  if (lang !== "ta") return uom;
  return UOM_TA[uom] || uom;
};
